import { ChangeDetectionStrategy, Component, input, output, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AllSmsActions, SmsAdminFilterKey, SmsStatus, SmsTag, ZoudashUtils } from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import { SmsActionDisplayPipe, SmsStatusDisplayPipe, SmsTagDisplayPipe } from '@workspace/ng-data-access';
import { SmsColumnKey } from '../abstract-sms-data-table.component';

export interface SmsFilterModel {
  action: string | string[] | null;
  tag: string | string[] | null;
  status: string | string[] | null;
}

@Component({
  selector: 'core-sms-data-table-filter',
  templateUrl: './sms-data-table-filter.component.html',
  imports: [
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatSlideToggleModule,
    SmsActionDisplayPipe,
    SmsTagDisplayPipe,
    SmsStatusDisplayPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmsDataTableFilterCoreComponent extends AbstractSignalFormComponent<
  Partial<Record<SmsAdminFilterKey, string | string[]>>,
  SmsFilterModel
> {
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<SmsColumnKey>[]>();
  filterChanged = output<FilterChangedDetection>();
  smsActionValues = Object.values(AllSmsActions);
  smsTagValues = Object.values(SmsTag);
  smsStatusValues = Object.values(SmsStatus);
  smsAdminFilterKey = SmsAdminFilterKey;

  override model: WritableSignal<SmsFilterModel> = signal<SmsFilterModel>({
    action: null,
    tag: null,
    status: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<SmsAdminFilterKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<SmsAdminFilterKey, string | string[]>>,
  ): SmsFilterModel {
    return {
      action: dto?.[SmsAdminFilterKey.Action] ?? null,
      tag: dto?.[SmsAdminFilterKey.Tag] ?? null,
      status: dto?.[SmsAdminFilterKey.Status] ?? null,
    };
  }

  protected override buildDtoFromModel(
    model: SmsFilterModel,
  ): Partial<Record<SmsAdminFilterKey, string | string[]>> {
    const dto: Partial<Record<SmsAdminFilterKey, string | string[]>> = {
      action: model.action ?? undefined,
      tag: model.tag ?? undefined,
      status: model.status ?? undefined,
    };
    return Object.fromEntries(Object.entries(dto).filter(([_, v]) => v != null)) as Partial<
      Record<SmsAdminFilterKey, string | string[]>
    >;
  }
}
